import { Box, ThemeProvider } from "@mui/material";
import { DEFAULT_VIEW_NAME, ROUTES } from "../../../utils/constants";
import { LayoutPage } from "../../layoutPage";
import { GridColDef, GridColumnOrderChangeParams, GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAuth } from "../../../redux/auth/authSlice";

import {
  IColumnVisibilityModel,
  PREF_REPORTS,
} from "../../../api/concierges/preferences";
import Preferences from "../../../components/preferences/prefereneces";
import {
  TableColumnMap,
  useHomesite,
} from "../../../components/core/homesites/homesiteColumns";
import {
  useHomeSiteObserver
} from "../../../reactQuery/homeSites/homsites/homeSiteQuery";
import ConciergeFilterComponent from "../concierge/ConciergeFilterComponent";
import { useNotesData } from "../../../reactQuery/homeSites/notes/homeSiteNotes";
import { useExceptionsData } from "../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { Note } from "../../../api/homeSiteNotes/types";
import { Exception } from "../../../api/homeSiteExceptions/types";
import { orderBy } from "lodash";
import {
  theme,
  CustomToolbar,
} from "../../../components/gridToolbarComponents/gridToolbarComponents";
import { getDataGridRowStyles } from "../../../components/datagrid/datagridStyles"
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
import { useQueryClient } from "@tanstack/react-query";
import { defaultPinned, pinnedSessionKey, sanitizePinned, selectedViewSessionKey } from "../../../utils/pinning";
import { useHomeSiteDataIndexQuery } from "../../../reactQuery/homeSitesDataIndex/homeSiteDataIndexQuery";
import { useBuyerListingData } from "../buyerListing/useBuyerListingData";
import { useDivisionFeatureFlags } from "../../../stateContext/featureFlags/FeatureFlagProvider";
import { syncRevisedEcoeDelayedReasonVisibility } from "../columnVisibilityUtils";

export const ClosingsReport = () => {
  const { getSortedColumns } = useHomesite();
  const reportsColumns = useMemo<GridColDef[]>(
    () =>
      getSortedColumns(
        false,
        ["homeSiteId", "communityName"],
        TableColumnMap.ReportFields
      ),
    []
  );
  const { userInfo } = useSelector(getAuth);
  const { showMilestoneProgress } = useDivisionFeatureFlags(userInfo?.userDivCode || "");

  const { data: homeSitesDataIndex, pending: isLoadingHomeSites } = useHomeSiteDataIndexQuery(userInfo.userDivCode);
  const { data: notes } = useNotesData(userInfo.userDivCode);
  const { data: exceptions } = useExceptionsData(userInfo.userDivCode);
  const { applyDynamicFilters, filteredHomesites } = useHomeSiteObserver(isLoadingHomeSites ? [] : (homeSitesDataIndex || []));
  const [enrichedRows, setEnrichedRows] = useState<HomeSitesDataIndex[]>([]);
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(defaultPinned(showMilestoneProgress));

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<IColumnVisibilityModel>({});
  // eslint-disable-next-line
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  const [orderedColumns, setOrderedColumns] = useState<string[]>([]);
  const [columnReordered, setColumnReordered] = useState(false);
  const { selectedViewFromBuyerListing } = useBuyerListingData();
  useEffect(() => {
    if (!filteredHomesites || filteredHomesites.length === 0) {
      setEnrichedRows([]);
      return;
    }

    // Build lookup maps for notes and exceptions - single pass
    const notesByHomeSiteId = new Map<string, Note[]>();
    const exceptionsByHomeSiteId = new Map<string, Exception[]>();

    if (notes?.data) {
      for (const note of notes.data) {
        if (!notesByHomeSiteId.has(note.homeSiteId)) {
          notesByHomeSiteId.set(note.homeSiteId, []);
        }
        notesByHomeSiteId.get(note.homeSiteId)!.push(note);
      }
    }

    if (exceptions?.data) {
      for (const exception of exceptions.data) {
        if (!exceptionsByHomeSiteId.has(exception.homeSiteId)) {
          exceptionsByHomeSiteId.set(exception.homeSiteId, []);
        }
        exceptionsByHomeSiteId.get(exception.homeSiteId)!.push(exception);
      }
    }

    // Pre-sort notes and exceptions by homeSiteId for faster lookup
    const sortedNotesByHomeSite = new Map<string, Note>();
    const sortedExceptionsByHomeSite = new Map<string, Exception>();

    for (const [homeSiteId, notesList] of notesByHomeSiteId.entries()) {
      const sorted = orderBy(notesList, [(n) => n.updateOnDate || n.createdOnDate], ["desc"]);
      if (sorted.length > 0) {
        sortedNotesByHomeSite.set(homeSiteId, sorted[0]);
      }
    }

    for (const [homeSiteId, exceptionsList] of exceptionsByHomeSiteId.entries()) {
      const sorted = orderBy(exceptionsList, [(e) => e.updatedOnDate || e.createdOnDate], ["desc"]);
      if (sorted.length > 0) {
        sortedExceptionsByHomeSite.set(homeSiteId, sorted[0]);
      }
    }

    // Build rows efficiently
    const rows: HomeSitesDataIndex[] = [];
    for (const row of filteredHomesites) {
      // Filter out homesites that have a sale date or trench date but no close of escrow date
      if (!row.closeOfEscrowActual && (row.trenchDate || row.saleDate)) {
        const latestNote = sortedNotesByHomeSite.get(row.homeSiteId);
        const latestException = sortedExceptionsByHomeSite.get(row.homeSiteId);

        rows.push({
          ...row,
          latestException: latestException
            ? `${latestException.mileStoneName} - ${latestException.description}`
            : "",
          latestNote: latestNote
            ? `${latestNote.mileStoneName} - ${latestNote.description}`
            : "",
        });
      }
    }

    setEnrichedRows(rows);
  }, [filteredHomesites, notes?.data, exceptions?.data]);
  /*
   * This function updates the column visibility model state with a new model when the column visibility changes.
   */
  const handleColumnVisibilityChange = (newModel: IColumnVisibilityModel) => {
    setColumnVisibilityModel((previousModel) =>
      syncRevisedEcoeDelayedReasonVisibility(newModel, previousModel)
    );
  };

  /*
   * This function handleColumnOrderChange updates the order of columns in a grid when a column is moved from one position to another.
   * It removes the column from its old index and inserts it at the target index, then updates the state with the new column order and sets a
   * flag indicating that the column order has changed.
   */
  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { oldIndex, targetIndex } = params;
    const left = pinnedColumns.left ?? [];
    const right = pinnedColumns.right ?? [];

    const allFieldsBase = reportsColumns.map(c => c.field);

    let currentFullOrder: string[];
    if (
      orderedColumns.length === 0 ||
      orderedColumns.length !== allFieldsBase.length ||
      !allFieldsBase.every(f => orderedColumns.includes(f))
    ) {
      const leftSet = new Set(left);
      const rightSet = new Set(right);
      const unpinned = allFieldsBase.filter(f => !leftSet.has(f) && !rightSet.has(f));
      currentFullOrder = [...left, ...unpinned, ...right];
    } else {
      currentFullOrder = [...orderedColumns];
    }

    const leftCount = left.length;
    const rightCount = right.length;
    const unpinnedCount = currentFullOrder.length - leftCount - rightCount;
    const lastUnpinnedIndex = leftCount + unpinnedCount - 1;

    const dragInUnpinned = oldIndex >= leftCount && oldIndex <= lastUnpinnedIndex;
    const dropInUnpinned = targetIndex >= leftCount && targetIndex <= lastUnpinnedIndex;
    if (!dragInUnpinned || !dropInUnpinned) return; // ignore drags involving pinned columns

    const unpinnedSlice = currentFullOrder.slice(leftCount, leftCount + unpinnedCount);
    const from = oldIndex - leftCount;
    const to = targetIndex - leftCount;
    const moving = unpinnedSlice.splice(from, 1)[0];
    unpinnedSlice.splice(to, 0, moving);

    const newFull = [...left, ...unpinnedSlice, ...right];
    setOrderedColumns(newFull);
    setColumnReordered(true);
  };

  useEffect(() => {
    // Which view is selected? (Defaults to Default View on this page)
    const selectedView = sessionStorage.getItem(selectedViewSessionKey(PREF_REPORTS)) ?? DEFAULT_VIEW_NAME;
    const key = pinnedSessionKey(PREF_REPORTS, selectedView);
    const fromSession = sessionStorage.getItem(key);
    if (fromSession) {
      try {
        setPinnedColumns(sanitizePinned(JSON.parse(fromSession), showMilestoneProgress));
      } catch (err) {
        console.error("Error parsing pinned columns from sessionStorage:", err);
      }
    }
  }, [showMilestoneProgress]);

  const handlePinnedColumnsChange = (next: GridPinnedColumnFields) => {
  const sanitized = sanitizePinned(next, showMilestoneProgress);
  setPinnedColumns(sanitized);

  // Save against the *currently selected view for this page
  const selectedView = sessionStorage.getItem(selectedViewSessionKey(PREF_REPORTS)) ?? DEFAULT_VIEW_NAME;
  const key = pinnedSessionKey(PREF_REPORTS, selectedView);
  sessionStorage.setItem(key, JSON.stringify(sanitized));
  };

  return (
    <ThemeProvider theme={theme}>
      <div>
        <LayoutPage title={ROUTES.closings.reports.label}>
          <p></p>
        </LayoutPage>
        <div
          style={{
            height: "100%",
            width: "100%",
            backgroundColor: "#fff",
            padding: "16px",
            borderRadius: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              marginBottom: "16px",
            }}
          >
            <ConciergeFilterComponent
              applyDynamicFilters={applyDynamicFilters}
            />
            <Preferences
              page={PREF_REPORTS}
              email={userInfo?.email}
              columns={reportsColumns}
              orderedColumns={orderedColumns}
              columnReordered={columnReordered}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              selectedViewFromBuyerListing={selectedViewFromBuyerListing}
              setOrderedColumns={setOrderedColumns}
              pinnedColumns={pinnedColumns}
              setPinnedColumns={setPinnedColumns}
            />
          </div>
          <Box
            sx={{
              "& .super-app-theme--header": {
                backgroundColor: "#DCE3E8",
              },
            }}
          >

            <div style={{ height: "75vh", width: "100%" }}>
              <DataGridPremium
                columns={
                  orderedColumns.length > 0
                    ? orderedColumns
                      .map((field) =>
                        reportsColumns.find((col) => col.field === field)
                      )
                      .filter((col): col is GridColDef => col !== undefined)
                    : reportsColumns
                }
                rows={enrichedRows}
                rowHeight={35} /* By default row height is 52px */
                pagination
                disableColumnPinning= {false}
                pinnedColumns={pinnedColumns}
                onPinnedColumnsChange={handlePinnedColumnsChange}
                disableAggregation
                disableRowGrouping
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25, page: 0 },
                  },
                  sorting: {
                    sortModel: [{ field: "coeEstimated", sort: "desc" }],
                  },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onColumnOrderChange={handleColumnOrderChange}
                slots={{ toolbar: () => CustomToolbar(true, true, true) }}
                sx={{
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: "#DCE3E8 !important",
                    color: "black",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    visibility: "visible",
                    color: "divider",
                  },
                  "& .MuiDataGrid-cell": {
                    borderRight: "1px solid rgba(224, 224, 224, 1)",
                  },
                  ...getDataGridRowStyles(theme, false),
                }}
                getRowClassName={(params) => {
                  return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
                }}
              />
            </div>

          </Box>
        </div>
      </div>
    </ThemeProvider>
  );
};
